// Android-приложение: игра (код, картинки, анимации) лежит внутри APK и открывается с адреса
// https://appassets.androidplatform.net; с сервером общается только API (данные игры).
// Натив встраивает мост window.KosApp до запуска скриптов страницы — по нему и узнаём, что мы в приложении.

interface KosBridge {
  versionCode(): number;
  versionName(): string;
  /** Канал установки: 'rustore' (обновления через RuStore) или 'site' (APK с сайта). */
  store(): string;
  /** Версия встроенного набора игры (обновляется без переустановки приложения). */
  bundleVersion(): string;
  openExternal(url: string): void;
  /** Окно входа Google/VK/Яндекс/Telegram — системный браузер поверх игры. */
  openAuth(url: string): void;
  share(text: string, url: string): void;
  exitApp(): void;
  // Уведомления — в приложении с версии 15; в старых APK этих методов нет, а свежая игра туда приходит
  // обновлением без переустановки, поэтому проверяем их наличие (см. lib/pwa.ts)
  notificationState?(): 'on' | 'off' | 'blocked';
  /** Системный запрос разрешения; ответ придёт событием window 'kos-notifications'. */
  requestNotifications?(): void;
  openNotificationSettings?(): void;
  pushToken?(): string;
  setPushToken?(token: string): void;
  showNotification?(title: string, body: string): void;
  /** Страница показала свой экран: убрать встроенный фон приложения (с версии 19). */
  bootReady?(): void;
}

declare global {
  interface Window {
    KosApp?: KosBridge;
    /** Кнопка «Назад» Android: true — обработали в игре, false — свернуть приложение. */
    __kosBack?: () => boolean;
  }
}

export const nativeApp: boolean = typeof window !== 'undefined' && !!window.KosApp;

/** Адрес сайта игры: API, ссылки на сайт и файлы, которых ещё нет в приложении. */
export const SITE_ORIGIN = 'https://kings-of-seas.ru';

/** Убрать стартовый фон из index.html (его сменяет экран загрузки или сама страница). */
export function removeBootSplash(): void {
  document.getElementById('boot-splash')?.remove();
  window.KosApp?.bootReady?.();
}

export function nativeBridge(): KosBridge | null {
  return typeof window !== 'undefined' ? (window.KosApp ?? null) : null;
}

/** Открыть ссылку вне игры (в приложении — системным браузером; относительные адреса — на сайте игры). */
export function openExternal(url: string): void {
  const bridge = nativeBridge();
  if (bridge) {
    bridge.openExternal(new URL(url, SITE_ORIGIN).toString());
    return;
  }
  window.open(url, '_blank', 'noopener');
}

/** Поделиться ссылкой: в приложении — системное окно Android, в браузере — Web Share (или false). */
export async function shareLink(title: string, text: string, url: string): Promise<boolean> {
  const bridge = nativeBridge();
  if (bridge) {
    bridge.share(text, url);
    return true;
  }
  if (navigator.share) {
    await navigator.share({ title, text, url }).catch(() => undefined);
    return true;
  }
  return false;
}

/** Корневые экраны: «Назад» на них сворачивает приложение. */
const ROOTS = new Set(['/', '/login', '/game', '/game/', '/game/city']);

/** Подготовка игры внутри приложения (вызывается один раз при старте). */
export function initNativeApp(): void {
  if (!nativeApp) return;
  document.documentElement.classList.add('native-app');
  // Внешние ссылки (window.open) — в системный браузер, а не поверх игры
  window.open = ((url?: string | URL) => {
    if (url) openExternal(String(url));
    return null;
  }) as typeof window.open;
  window.__kosBack = () => {
    // Открытое окно (диалог, лист) закрывается как по Esc
    const dialog = document.querySelector('[role="dialog"][aria-modal="true"]');
    if (dialog) {
      document.dispatchEvent(new KeyboardEvent('keydown', { key: 'Escape', bubbles: true }));
      return true;
    }
    if (ROOTS.has(window.location.pathname) || window.history.length <= 1) return false;
    window.history.back();
    return true;
  };
}
